import type { GameLike, GameOfficialRef } from './types'

/**
 * How a game's officials split into the three columns the Details grid shows.
 *
 * A game's officials are stored as (person, role) PAIRS, not as a set of people:
 * two referees are two people holding the same role, and one person can hold two
 * roles on the same game (23,832 games in production do). Every helper here is
 * therefore keyed on the pair.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * DUPLICATED GROUPING — the api2 model owns the other copy:
 *   Game::referees()      api2/app/Models/Game.php:444
 *   Game::timekeepers()   api2/app/Models/Game.php:459
 *   Game::scorekeepers()  api2/app/Models/Game.php:474
 * A new `official_type` row has to be added in BOTH places or it will be
 * invisible in one of them. `bucketForType` returning null is the signal that
 * this file has fallen behind.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/** `official_type.id`. Fixed values, mirrored from api2's `OfficialType` constants. */
export const OFFICIAL_TYPE = {
  REFEREE: 1,
  SCOREKEEPER: 2,
  TIMEKEEPER: 3,
  HEAD_REFEREE: 7,
  ASSISTANT_REFEREE: 8,
  FOURTH_OFFICIAL: 9,
} as const

export type OfficialBucket = 'referees' | 'scorekeepers' | 'timekeepers'

/** Column order, left to right. */
export const OFFICIAL_BUCKETS: OfficialBucket[] = [
  'referees',
  'scorekeepers',
  'timekeepers',
]

/**
 * The roles each column owns, and — first in each list — the role a newly added
 * person defaults to when their own stored role belongs to another column.
 *
 * Fourth Official sits with the scorekeepers, matching `Game::scorekeepers()`.
 * It reads oddly, but the backend has grouped it that way since the type was
 * introduced and splitting it here would put the same row in two columns.
 */
export const OFFICIAL_BUCKET_TYPES: Record<OfficialBucket, number[]> = {
  referees: [
    OFFICIAL_TYPE.REFEREE,
    OFFICIAL_TYPE.HEAD_REFEREE,
    OFFICIAL_TYPE.ASSISTANT_REFEREE,
  ],
  scorekeepers: [OFFICIAL_TYPE.SCOREKEEPER, OFFICIAL_TYPE.FOURTH_OFFICIAL],
  timekeepers: [OFFICIAL_TYPE.TIMEKEEPER],
}

/** One (person, role) pair on one game. */
export interface OfficialAssignment {
  /** The official's uid — what the API takes and returns. */
  officialId: string
  /** The role for THIS game, not the role stored on the person. */
  officialTypeId: number
}

export const primaryTypeForBucket = (bucket: OfficialBucket): number =>
  OFFICIAL_BUCKET_TYPES[bucket][0]

/**
 * Which column a role belongs to, or null when it belongs to none.
 *
 * Null is a real answer, not an error case: a role seeded on the backend and not
 * added here would otherwise be silently dropped from the grid, and an admin
 * would see an official vanish from a game that still has them.
 */
export const bucketForType = (
  officialTypeId: number | null | undefined
): OfficialBucket | null => {
  if (officialTypeId == null) {
    return null
  }
  const match = OFFICIAL_BUCKETS.find((bucket) =>
    OFFICIAL_BUCKET_TYPES[bucket].includes(Number(officialTypeId))
  )
  return match ?? null
}

const toAssignment = (official: GameOfficialRef): OfficialAssignment => ({
  officialId: official.id,
  officialTypeId: Number(official.official_type_id),
})

/**
 * The game's assignments for one column, in the order the API sent them.
 *
 * An absent `officials` array yields an empty list — the caller cannot tell
 * "nobody asked for officials" from "this game has none" here, and does not need
 * to: both render as an empty cell.
 */
export const assignmentsForBucket = (
  game: GameLike | null | undefined,
  bucket: OfficialBucket
): OfficialAssignment[] =>
  (game?.officials ?? [])
    .filter((official) => bucketForType(official.official_type_id) === bucket)
    .map(toAssignment)

/** Every assignment whose role belongs to no column. Nothing renders these yet. */
export const unbucketedAssignments = (
  game: GameLike | null | undefined
): OfficialAssignment[] =>
  (game?.officials ?? [])
    .filter((official) => bucketForType(official.official_type_id) === null)
    .map(toAssignment)

const assignmentKey = (assignment: OfficialAssignment): string =>
  `${assignment.officialId}:${assignment.officialTypeId}`

/**
 * Set equality over (person, role) pairs — order and duplicates ignored.
 *
 * Load-bearing for the "nothing changed" skip: the editor always commits a fresh
 * array, so AG Grid reports a cell change even when the admin opened the editor
 * and pressed Apply without touching anything.
 */
export const sameAssignments = (
  left: OfficialAssignment[],
  right: OfficialAssignment[]
): boolean => {
  const leftKeys = new Set(left.map(assignmentKey))
  const rightKeys = new Set(right.map(assignmentKey))
  if (leftKeys.size !== rightKeys.size) {
    return false
  }
  return Array.from(leftKeys).every((key) => rightKeys.has(key))
}

/** Drop repeated (person, role) pairs, keeping first-seen order. */
export const dedupeAssignments = (
  assignments: OfficialAssignment[]
): OfficialAssignment[] => {
  const seen = new Set<string>()
  return assignments.filter((assignment) => {
    const key = assignmentKey(assignment)
    if (seen.has(key)) {
      return false
    }
    seen.add(key)
    return true
  })
}

export interface AssignmentLabelContext {
  /** uid → display name. Flat and row-independent by design — see below. */
  nameFor: (officialId: string) => string
  /** `official_type.id` → display name, e.g. 7 → "Head Referee". */
  roleFor: (officialTypeId: number) => string
  bucket: OfficialBucket
}

/**
 * What a bucket cell reads as: "Craig Jenner, Rob Larsen (Head Referee)".
 *
 * The role is appended ONLY when it differs from the column's primary role. The
 * column header already says "Referees", so tagging every chip with "Referee"
 * is noise — but an override is exactly the thing a reader needs to see.
 *
 * Resolves from the ASSIGNMENTS ALONE, never from a row. AG Grid hands a column
 * formatter no row when it is labelling something other than a cell, and a
 * formatter that reaches for `params.data` renders the same label everywhere.
 */
export const labelAssignments = (
  assignments: OfficialAssignment[] | null | undefined,
  context: AssignmentLabelContext
): string => {
  if (assignments == null || assignments.length === 0) {
    return ''
  }
  const primaryType = primaryTypeForBucket(context.bucket)

  return assignments
    .map((assignment) => {
      const name = context.nameFor(assignment.officialId)
      if (assignment.officialTypeId === primaryType) {
        return name
      }
      const role = context.roleFor(assignment.officialTypeId)
      return role === '' ? name : `${name} (${role})`
    })
    .filter((label) => label !== '')
    .join(', ')
}
